import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { formatAuDateShort, formatAuDateTime } from "@/lib/date";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Copy, MessageCircle, MailCheck, Flag, ShieldCheck } from "lucide-react";

function ReasonBadge({ reason }) {
  let cls = "bg-indigo-100 text-indigo-800 border-indigo-200";
  if (reason === "DECLINED EMAIL") cls = "bg-red-100 text-red-800 border-red-200";
  else if (reason === "REQUESTED POSITION NOT AVAILABLE") cls = "bg-amber-100 text-amber-800 border-amber-200";
  else if (reason === "AWAITING ORGANISER ALLOCATION") cls = "bg-blue-100 text-blue-800 border-blue-200";
  else if (reason === "REGISTERED — NOT YET CONFIRMED") cls = "bg-yellow-100 text-yellow-900 border-yellow-300";
  return <span className={`inline-block border px-2 py-1 text-[10px] font-black uppercase tracking-widest ${cls}`}>{reason}</span>;
}

export function ChaseList({ rows, onFollowupToggled, eventId }) {
  const [selected, setSelected] = useState(new Set());
  useEffect(() => {
    const valid = new Set(rows.map((r) => r.volunteer_id));
    setSelected((prev) => new Set([...prev].filter((id) => valid.has(id))));
  }, [rows]);

  function toggle(id) { setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; }); }
  function toggleAll() { if (selected.size === rows.length) setSelected(new Set()); else setSelected(new Set(rows.map((r) => r.volunteer_id))); }

  function formatBlock(list) {
    return list.map((r) => {
      const req = r.requested || {}, ass = r.assigned || {};
      const slot = ass.day ? `${ass.day} ${ass.session} ${ass.role}` : req.day ? `requested ${req.day} ${req.session} ${req.role}` : "";
      return `${r.name}\n  ${r.mobile}  ${r.email}\n  Reason: ${r.reason}${slot ? `  |  ${slot}` : ""}`;
    }).join("\n\n");
  }
  async function copyText(text, label) {
    try { await navigator.clipboard.writeText(text); toast.success(`${label} copied`); }
    catch { const ta = document.createElement("textarea"); ta.value = text; document.body.appendChild(ta); ta.select(); document.execCommand("copy"); ta.remove(); toast.success(`${label} copied`); }
  }
  function copySelected() { const list = rows.filter((r) => selected.has(r.volunteer_id)); if (!list.length) return toast.info("No rows selected"); copyText(formatBlock(list), `${list.length} contact${list.length > 1 ? "s" : ""}`); }
  function copyAll() { if (!rows.length) return; copyText(formatBlock(rows), `All ${rows.length} contact${rows.length > 1 ? "s" : ""}`); }
  function whatsappHref(row) {
    const digits = String(row.mobile || "").replace(/[^\d]/g, "");
    const msg = encodeURIComponent(`Hi ${row.name}, follow-up from VOLMEE about your volunteer registration (${row.reason.toLowerCase()}).`);
    return `https://wa.me/${digits}?text=${msg}`;
  }

  if (!rows.length) {
    return <div className="border border-black/10 bg-white p-10 text-center text-sm text-zinc-500" data-testid="chase-list-empty">No one to chase yet.</div>;
  }
  const allChecked = selected.size === rows.length && rows.length > 0;
  return (
    <div className="border border-black/10 bg-white">
      <div className="flex flex-wrap items-center gap-3 border-b border-black/10 bg-[#F4F4F5] px-4 py-3">
        <span className="text-xs font-semibold text-zinc-700" data-testid="chase-selected-count">{selected.size} of {rows.length} selected</span>
        <div className="ml-auto flex flex-wrap gap-2">
          <button onClick={copySelected} disabled={selected.size === 0} data-testid="copy-selected-btn" className="inline-flex items-center gap-1.5 border border-black/20 bg-white px-3 py-1.5 text-xs font-medium hover:bg-black hover:text-white disabled:opacity-40">
            <Copy className="h-3.5 w-3.5" /> Copy selected
          </button>
          <button onClick={copyAll} data-testid="copy-all-btn" className="inline-flex items-center gap-1.5 border border-black bg-black px-3 py-1.5 text-xs font-medium text-white hover:bg-white hover:text-black">
            <Copy className="h-3.5 w-3.5" /> Copy all
          </button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-black text-white">
              <th className="w-10 px-3 py-2 text-left"><Checkbox checked={allChecked} onCheckedChange={toggleAll} data-testid="chase-select-all" className="border-white/40 data-[state=checked]:bg-white data-[state=checked]:text-black" /></th>
              {["Name", "Email", "Mobile", "Reason", "Requested", "Assigned", "Responded", "Action"].map((h) => (<th key={h} className="px-3 py-2 text-left font-display text-xs font-black uppercase tracking-widest">{h}</th>))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const req = r.requested || {}, ass = r.assigned || {};
              const rs = selected.has(r.volunteer_id);
              return (
                <tr key={r.volunteer_id} data-testid={`chase-row-${r.volunteer_id}`} className={`border-b border-black/5 ${rs ? "bg-blue-50/60" : ""} ${r.has_time_conflict && !r.conflict_approved ? "bg-red-50/70" : ""}`}>
                  <td className="px-3 py-3"><Checkbox checked={rs} onCheckedChange={() => toggle(r.volunteer_id)} data-testid={`chase-select-${r.volunteer_id}`} /></td>
                  <td className="px-3 py-3 font-semibold">
                    <div className="flex items-center gap-2">
                      {r.has_time_conflict && !r.conflict_approved ? (
                        <span data-testid={`chase-conflict-flag-${r.volunteer_id}`} title="Double-booked — this volunteer has two overlapping slots on the same day" className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#FF3B30] text-white">
                          <Flag className="h-3 w-3" />
                        </span>
                      ) : null}
                      {r.has_time_conflict && r.conflict_approved ? (
                        <span data-testid={`chase-conflict-approved-${r.volunteer_id}`} title={`Conflict approved by ${r.conflict_approved_by || 'admin'}`} className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-white">
                          <ShieldCheck className="h-3 w-3" />
                        </span>
                      ) : null}
                      <span>{r.name}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-zinc-700">{r.email}</td>
                  <td className="px-3 py-3 font-mono text-xs">{r.mobile}</td>
                  <td className="px-3 py-3"><ReasonBadge reason={r.reason} /></td>
                  <td className="px-3 py-3 font-mono text-xs text-zinc-700">{req.day ? `${formatAuDateShort(req.day)} · ${req.session} · ${req.role}` : "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-zinc-700">{ass.day ? `${formatAuDateShort(ass.day)} · ${ass.session} · ${ass.role}` : "—"}</td>
                  <td className="px-3 py-3 font-mono text-xs text-zinc-500">{r.confirmed_at ? formatAuDateTime(r.confirmed_at) : "—"}</td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <button onClick={() => copyText(formatBlock([r]), r.name)} data-testid={`chase-copy-${r.volunteer_id}`} className="inline-flex items-center gap-1 border border-black/20 px-2 py-1 text-[11px] hover:bg-black hover:text-white"><Copy className="h-3 w-3" /> Copy</button>
                      <a href={whatsappHref(r)} target="_blank" rel="noreferrer" data-testid={`chase-whatsapp-${r.volunteer_id}`} className="inline-flex items-center gap-1 border border-emerald-600/40 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-800 hover:bg-emerald-600 hover:text-white"><MessageCircle className="h-3 w-3" /> WhatsApp</a>
                      <button onClick={async () => {
                        const defaultMsg = `Hi ${r.name}, follow-up from VOLMEE about your volunteer registration (${r.reason.toLowerCase()}). Please reply to confirm.`;
                        const msg = window.prompt("SMS message (mocked):", defaultMsg);
                        if (!msg) return;
                        try { await api.post(`/admin/events/${eventId}/sms/send`, { volunteer_id: r.volunteer_id, message: msg }); toast.success(`Mock SMS queued to ${r.mobile}`); }
                        catch (e) { toast.error(e.response?.data?.detail || "SMS failed"); }
                      }} data-testid={`chase-sms-${r.volunteer_id}`} className="inline-flex items-center gap-1 border border-[#002FA7]/40 bg-blue-50 px-2 py-1 text-[11px] text-[#002FA7] hover:bg-[#002FA7] hover:text-white"><MessageCircle className="h-3 w-3" /> SMS</button>
                      <button onClick={async () => {
                        try {
                          const { data } = await api.post(`/admin/events/${eventId}/volunteers/${r.volunteer_id}/resend-confirmation`, { email: true, sms: false });
                          if (data.email_sent) toast.success(`Confirmation email resent to ${r.email}`);
                          else toast.info("Nothing to resend (no pending confirmation)");
                        } catch (e) {
                          toast.error(e.response?.data?.detail || "Resend failed");
                        }
                      }} data-testid={`chase-resend-conf-${r.volunteer_id}`} className="inline-flex items-center gap-1 border border-violet-500/40 bg-violet-50 px-2 py-1 text-[11px] text-violet-800 hover:bg-violet-600 hover:text-white"><MailCheck className="h-3 w-3" /> Resend Conf' Email</button>
                      <button onClick={async () => { await api.post(`/admin/events/${eventId}/volunteers/${r.volunteer_id}/followup`); onFollowupToggled?.(); }} data-testid={`chase-clear-${r.volunteer_id}`} className="border border-black/20 px-2 py-1 text-[11px] hover:bg-black hover:text-white">Clear flag</button>
                      {r.has_time_conflict && !r.conflict_approved ? (
                        <button
                          onClick={async () => {
                            if (!window.confirm(`Approve ${r.name} being in two overlapping roles?\n\nThis marks the conflict as reviewed by you. The red flag will disappear.`)) return;
                            try {
                              await api.post(`/admin/events/${eventId}/volunteers/${r.volunteer_id}/approve-conflict`);
                              toast.success("Conflict approved — the red flag is now cleared.");
                              onFollowupToggled?.();
                            } catch (e) {
                              toast.error(e.response?.data?.detail || "Approve failed");
                            }
                          }}
                          data-testid={`chase-approve-conflict-${r.volunteer_id}`}
                          className="inline-flex items-center gap-1 border border-emerald-600 bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-white hover:text-emerald-700"
                        ><ShieldCheck className="h-3 w-3" /> Approve conflict</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
